import React, { ReactNode } from 'react';
import { Field } from 'react-final-form';
import { I18n, Translate } from 'react-redux-i18n';
import image from '../../../../../img/structure-pro/horaires.svg';
import TimeInput from '../../../../common/inputs/TimeInput';
import { OpeningHours } from '../../../../../network/api/types/compte/OpeningHours';
import {
  getAllHours, SimpleTime, getNextTimeSlot, toSimpleTimes,
} from '../../../../../services/timeService';
import { TimeSlot } from '../../../../../network/api/types/compte/TimeSlot';
import { Day } from '../../../../../network/api/types/compte/Day';
import Button from '../../../../common/Button';

type Props = {
  openingHours: OpeningHours
  changeHours: (
    dayIndex: number,
    hoursIndex: number,
    startH?: number,
    startM?: number,
    finishH?: number,
    finishM?: number,
  ) => void
  removeHours: (dayIndex: number, hoursIndex: number) => void
  defaultClosed: boolean[]
  isManager: boolean
};

const MinutesStep = 5;

const AllHours = getAllHours(MinutesStep);

class StructureOpeningHours extends React.Component<Props> {
  startOrFinish = (name: string): 'start' | 'finish' => (name.includes('start') ? 'start' : 'finish');

  selectDayOfWeek = (name: string, type?: 'start' | 'finish'): string => {
    const tipe = type || this.startOrFinish(name);
    return name.slice(0, name.indexOf(tipe));
  };

  selectHoursIndex = (name: string, type: 'start' | 'finish'): number => parseInt(name.split(type)[1], 10);

  selectSources = (day: Day, type: 'start' | 'finish', hoursIndex: number):SimpleTime[] => {
    const retMinutes = AllHours.map((simpleTime) =>
      parseInt(simpleTime.minutes, 10) + parseInt(simpleTime.hours, 10) * 60);
    const currentHours: TimeSlot = day.openingHours[hoursIndex];
    let startSlice;
    let stopSlice;
    if (type === 'start') {
      const previousHours: TimeSlot = hoursIndex > 0 ? day.openingHours[hoursIndex - 1] : null;
      startSlice = previousHours
        ? retMinutes.indexOf(previousHours.finishM + previousHours.finishH * 60) + 1 : 0;
      stopSlice = retMinutes.indexOf(currentHours.finishM + currentHours.finishH * 60);
    } else {
      const nextHours: TimeSlot = hoursIndex < day.openingHours.length - 1
        ? day.openingHours[hoursIndex + 1] : null;
      startSlice = retMinutes.indexOf(currentHours.startM + currentHours.startH * 60) + 1;
      stopSlice = nextHours
        ? retMinutes.indexOf(nextHours.startM + nextHours.startH * 60) : retMinutes.length;
    }
    return AllHours.slice(startSlice, stopSlice);
  };

  changeHours = (time: string, name: string): void => {
    const typeOfHours = this.startOrFinish(name);
    const dayOfWeek = this.selectDayOfWeek(name, typeOfHours);
    const dayIndex = this.props.openingHours.days.findIndex((day) => day.dayOfWeek === dayOfWeek);
    const hoursIndex = this.selectHoursIndex(name, typeOfHours);
    const split = time.split(':');
    if (typeOfHours === 'start') {
      this.props.changeHours(dayIndex, hoursIndex, parseInt(split[0], 10), parseInt(split[1], 10));
    } else {
      this.props.changeHours(dayIndex, hoursIndex, null, null,
        parseInt(split[0], 10), parseInt(split[1], 10));
    }
  };

  addHours = (day: Day, dayIndex: number): void => {
    const newHoursIndex = day.openingHours.length;
    const addHours = getNextTimeSlot(day.openingHours[day.openingHours.length - 1], MinutesStep);
    if (Object.prototype.hasOwnProperty.call(addHours, 'startM')) {
      this.props.changeHours(dayIndex, newHoursIndex,
        addHours.startH,
        addHours.startM,
        addHours.finishH,
        addHours.finishM);
    }
  };

  render(): ReactNode {
    if (!this.props.openingHours) {
      return false;
    }
    return (
      <div className="structure-container structure-contact">
        <div className="structure-illustration-container">
          <div className="container-img structure-illustration">
            <img
              className="structure-illustration"
              src={image}
              alt="ILLUSTRATION"
            />
          </div>
        </div>
        <div className="structure-drawer-title-container no-grow">
          <div className="structure-drawer-title">
            <Translate value="STRUCTURE_INFORMATION.OPENING_HOURS" />
          </div>
          <div className="structure-opening-hours-form">
            {this.props.openingHours?.days.map((day, dayIndex) => {
              const dayOfWeek = day.dayOfWeek;
              const closedInput = document.getElementById(`closed${dayOfWeek}`) as HTMLInputElement;
              return (
                <div key={dayIndex}>
                  <div className="opening-hours-day-dummy-line">
                    <div className="opening-hours-day">
                      <Translate value={`STRUCTURE_INFORMATION.DAYS.${dayOfWeek}`} />
                    </div>
                  </div>
                  <div key={this.props.defaultClosed[dayIndex] + dayOfWeek} className={`opening-hours-day-line ${closedInput?.checked ? 'closed' : ''}`}>
                    {day.openingHours.map((timeSlot, hoursIndex) => {
                      const simpleTimes = toSimpleTimes(timeSlot);
                      const start = `${simpleTimes[0].hours}:${simpleTimes[0].minutes}`;
                      const finish = `${simpleTimes[1].hours}:${simpleTimes[1].minutes}`;
                      const startName = `${dayOfWeek}start${hoursIndex}`;
                      const finishName = `${dayOfWeek}finish${hoursIndex}`;
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className="opening-hours-timeslot-line" key={hoursIndex}>
                          <div className="opening-hours-day">
                            {!hoursIndex && (<Translate value={`STRUCTURE_INFORMATION.DAYS.${dayOfWeek}`} />)}
                          </div>
                          <Field
                            name={startName}
                            value={start}
                            render={(props) => (
                              <TimeInput
                                input={props.input}
                                meta={props.meta}
                                value={start}
                                selectSources={this.selectSources(day, 'start', hoursIndex)}
                                day={dayOfWeek}
                                onChange={this.changeHours}
                                disabled={!this.props.isManager}
                              />
                            )}
                          />
                          <span className="between-times">-</span>
                          <Field
                            name={finishName}
                            value={finish}
                            render={(props) => (
                              <TimeInput
                                input={props.input}
                                meta={props.meta}
                                value={finish}
                                selectSources={this.selectSources(day, 'finish', hoursIndex)}
                                day={dayOfWeek}
                                onChange={this.changeHours}
                                disabled={!this.props.isManager}
                              />
                            )}
                          />
                          {!hoursIndex
                            ? (
                              <Field
                                type="checkbox"
                                name={`closed${dayOfWeek}`}
                                placeholder={I18n.t('STRUCTURE_INFORMATION.CLOSED')}
                                render={({ input: { onChange } }) => (
                                  <label
                                    className="closed-day-button"
                                    htmlFor={`closed${dayOfWeek}`}
                                  >
                                    <input
                                      key={dayOfWeek}
                                      type="checkbox"
                                      name={`closed${dayOfWeek}`}
                                      id={`closed${dayOfWeek}`}
                                      defaultChecked={this.props.defaultClosed[dayIndex]}
                                      onClick={onChange}
                                      disabled={!this.props.isManager}
                                    />
                                    <span className="custom-checkbox" />
                                    <Translate value="STRUCTURE_INFORMATION.CLOSED" />
                                  </label>
                                )}
                              />
                            ) : (
                              this.props.isManager
                              && (
                              <button
                                type="button"
                                onClick={() => this.props.removeHours(dayIndex, hoursIndex)}
                                className="delete-hours-line-button"
                              >
                                <i className="fa fa-times" />
                              </button>
                              ))}
                        </div>
                      );
                    })}
                  </div>
                  {this.props.isManager
                  && (
                  <div className="add-hours-button">
                    <div className="opening-hours-day" />
                    <Button
                      type="secondary"
                      onClick={() => this.addHours(day, dayIndex)}
                    >
                      <Translate value="STRUCTURE_INFORMATION.ADD_HOURS" />
                    </Button>
                  </div>
                  )}
                </div>
              );
            }
              )}
          </div>
        </div>
      </div>
    );
  }
}

export default StructureOpeningHours;
